/* IntelApps — scroll-triggered section reveals (all pages under <main>) */
import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SELECTOR = 'main section.section, main section.page-hero, main section.hero';

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function SectionScrollReveal() {
  const location = useLocation();

  useLayoutEffect(() => {
    const sections = document.querySelectorAll(SELECTOR);
    if (!sections.length) return undefined;

    if (prefersReducedMotion()) {
      sections.forEach((el) => {
        el.classList.remove('section-reveal', 'is-revealed');
        el.style.removeProperty('--reveal-stagger');
      });
      return undefined;
    }

    const vh = window.innerHeight || document.documentElement.clientHeight;
    const foldLine = vh * 0.88;

    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: '6% 0px -10% 0px',
        threshold: [0, 0.05, 0.1],
      },
    );

    let belowFoldIndex = 0;
    sections.forEach((el) => {
      el.classList.remove('section-reveal', 'is-revealed');
      const { top } = el.getBoundingClientRect();
      if (top > foldLine) {
        const staggerMs = Math.min(belowFoldIndex, 14) * 52;
        el.style.setProperty('--reveal-stagger', `${staggerMs}ms`);
        belowFoldIndex += 1;
        el.classList.add('section-reveal');
        io.observe(el);
      } else {
        el.style.removeProperty('--reveal-stagger');
      }
    });

    return () => {
      io.disconnect();
      sections.forEach((el) => {
        el.classList.remove('section-reveal', 'is-revealed');
        el.style.removeProperty('--reveal-stagger');
      });
    };
  }, [location.pathname]);

  return null;
}
